import { api } from '../../services/api';

export type SiteConditionMeasurementBucket = {
  measurementBucketId: string;
  windowKind: string;
  resolution: string;
  activityClassification: string;
  measurementValue: number | null;
  usableCameraSeconds: number | null;
  expectedCameraSeconds: number | null;
  displayState: string;
  freshnessState: string;
  bucketFinality: string;
  isPartial: boolean;
  observedThrough: string | null;
  displayAsOf: string | null;
  /** Included OM interval; `end` is bucket_end, not the rolling envelope. */
  interval?: {
    start: string | null;
    end: string | null;
  } | null;
  reasonCodes: string[];
};

/** Latest PT15M bucket that already has a numeric measurement. Not a claim numerator. */
export function latestCompletedPt15mMeasurementValue(
  buckets: Array<Pick<SiteConditionMeasurementBucket, 'resolution' | 'measurementValue' | 'interval' | 'displayAsOf'>> | null | undefined,
): number | null {
  const completed = (buckets || []).filter((bucket) => {
    const resolution = String(bucket.resolution || '').trim().toUpperCase();
    return resolution === 'PT15M'
      && typeof bucket.measurementValue === 'number'
      && Number.isFinite(bucket.measurementValue);
  });
  if (!completed.length) return null;
  const ranked = [...completed].sort((left, right) => {
    const leftEnd = Date.parse(String(left.interval?.end || left.displayAsOf || '')) || 0;
    const rightEnd = Date.parse(String(right.interval?.end || right.displayAsOf || '')) || 0;
    return leftEnd - rightEnd;
  });
  const value = ranked[ranked.length - 1]?.measurementValue;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export type SiteConditionMeasurementCoverage = {
  measurementStatus: string;
  usableHours: number | null;
  expectedHours: number | null;
  coveragePct: number | null;
  reviewedObservationCount: number | null;
  ratePerHour: number | null;
  /** Envelope producer lag flag — passthrough only (null/omitted when N/A). */
  producerStaleness?: boolean | null;
  /** Contiguous trailing TELEMETRY_MISSING outage start — not reading age. */
  telemetryMissingSince?: string | null;
  /** Duration of that trailing telemetry outage in hours. */
  telemetryMissingHours?: number | null;
  basisVersion: string | null;
  basisId: string | null;
  coverageGatePassed?: boolean | null;
  reviewGatePassed?: boolean | null;
  blockedBy?: string[];
  pendingReviewCount?: number | null;
  confirmedCount?: number | null;
  rejectedCount?: number | null;
  itemCount?: number | null;
  reviewWindowState?: string | null;
  reviewSetId?: string | null;
  reviewCompleteThrough?: string | null;
  oldestPendingItemTime?: string | null;
  frozenBasis: {
    conditionId: string | null;
    canonicalScope: Record<string, unknown>;
    windowStart: string | null;
    windowEnd: string | null;
    basisType: string | null;
    basisUnits: string | null;
    overlapDedupRule: string | null;
    basisVersion: string | null;
    basisId: string | null;
    siteTimezone: string | null;
    scheduleTimezone: string | null;
    shiftCalendarVersion: string | null;
    scheduleCadence: string | null;
    expectedHoursSource: string | null;
    usableHoursSource: string | null;
    reviewedObservationCountSource: string | null;
  } | null;
  shortfallReasonBreakdown: Record<string, {
    bucketCount: number;
    expectedHours: number | null;
    usableHours: number | null;
    shortfallHours: number | null;
  }>;
  /** EPIC284 Task 3 — mutually exclusive duration partition (reconciles to schedule). */
  coveragePartition?: {
    usableSeconds: number;
    telemetryUnavailableSeconds: number;
    unanalyzableSeconds: number;
    sourceUnavailableSeconds: number;
    unknownSeconds: number;
    usableHours: number | null;
    telemetryUnavailableHours: number | null;
    unanalyzableHours: number | null;
    sourceUnavailableHours: number | null;
    unknownHours: number | null;
  } | null;
  /** Overlapping diagnostics (e.g. insufficient_history); not additive. */
  coverageDiagnostics?: Array<{
    code: string;
    affectedSeconds: number | null;
    affectedHours: number | null;
    bucketCount: number;
    overlapsPartition: boolean;
  }>;
  coverageReconciliation?: {
    reconciled: boolean | null;
    scheduledSeconds: number | null;
    partitionSeconds: number | null;
    toleranceSeconds: number | null;
    deltaSeconds: number | null;
    invalidReason: string | null;
  } | null;
  coverageReconciliationInvalid?: boolean;
};

/**
 * EPIC284 Task 2.2 — identity keys for a displayed operational result.
 * Twin/window swaps must change these keys; never treat family-only labels as identity.
 */
export type OperationalMeasurementProvenance = {
  windowId: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  scopeId: string | null;
  conditionId: string | null;
  conditionDefinitionVersion: string | null;
  episodeDefinitionVersion: string | null;
  denominatorPolicyVersion: string | null;
  inclusionPolicyVersion: string | null;
  sourceRevision: string | null;
  unavailableReason: string | null;
};

export type SiteConditionMeasurementWindow = {
  availability: string;
  displayState: string;
  comparisonStatus?: string;
  windowAnchorId?: string | null;
  /** CCR implementation timestamp for this window anchor; never measurement time. */
  anchorStartedAt?: string | null;
  /** Window-level as-of when API provides it (K1). */
  displayAsOf?: string | null;
  observedThrough?: string | null;
  /**
   * Recurring History-as-of cursor — selected/anchored window end when API emits it.
   * Prefer over earliest-bucket displayAsOf for longitudinal "History as of".
   */
  historyAsOf?: string | null;
  /**
   * Optional readiness envelope fields — passthrough only when server emits them.
   * Never invent client-side (EPIC283-B).
   */
  updateMode?: string | null;
  nextTrigger?: string | null;
  nextTriggerNullReason?: string | null;
  sourceAge?: string | number | null;
  stageLatency?: string | number | null;
  basis: {
    type: string;
    units: string;
    version: string | null;
    measurementEpoch: string | null;
  } | null;
  /** Operational identity keys — present when K1 emits them. */
  operationalMeasurementProvenance?: OperationalMeasurementProvenance | null;
  /**
   * Optional paired baseline coverage for Since Control comparison issuable.
   * Passthrough only when the server emits `comparison_baseline_coverage`.
   * Never derive baseline rates or hours in the client.
   */
  comparisonBaselineCoverage?: SiteConditionMeasurementCoverage | null;
  summary: {
    bucketCount: number;
    measurementValue: number | null;
    activityCounts?: Record<string, number>;
    measurementCoverage?: SiteConditionMeasurementCoverage | null;
  };
  buckets: SiteConditionMeasurementBucket[];
  reasonCodes: string[];
  /** EPIC293: authoritative recurrence measurement facts (not a policy verdict). */
  recurrenceBasis?: SiteConditionRecurrenceBasis | null;
};

export type SiteConditionRecurrenceBasis = {
  schemaVersion: number;
  windowId: string;
  measuredPeriods: number;
  expectedPeriods: number | null;
  historyCompleteness: string;
  periodsWithActivity: number;
  periodsWithExplicitZero: number;
  occurrenceMeasuredSeconds: number;
  recurringRateState: string;
  activityPresent: boolean;
  supportingActivityTotal: number | null;
  recordedOccurrenceTotal?: number | null;
  usableCameraSeconds?: number | null;
  usableHours?: number | null;
  recurringRate?: number | null;
  includedBucketCount?: number | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  basisEnd: string | null;
  materialToken: string | null;
  basis: string;
};

export type SiteConditionAttentionState = {
  attentionRequired: boolean;
  attentionStatus: string | null;
  attentionReason: string | null;
  attentionReasons: string[];
  attentionPolicyId: string | null;
  attentionPolicyVersion: number | null;
  attentionTriggeredAt: string | null;
  lastAttentionActivityAt: string | null;
  lastReasonUpdatedAt: string | null;
  reasonState: Record<string, unknown>;
  attentionEvaluatedAt: string | null;
  attentionScopeKey: string | null;
  attentionLatchId: string | null;
  attentionEvidenceRefs: Record<string, unknown>;
  openWorkRef: Record<string, unknown> | null;
};

export type SiteConditionCurrentActivity = {
  conditionId: string | null;
  cameraId: string | null;
  zoneId: string | null;
  policyId: string | null;
  windowStart: string | null;
  queryAsOf: string | null;
  occurrenceCountSoFar: number | null;
  latestQualifiedAt: string | null;
  provisional: true;
  availability: 'available' | 'no_activity' | 'unavailable';
  reasonCodes: string[];
};

export type SiteConditionOperationalScope = {
  areaId: string | null;
  deviceId: string | null;
  cameraId: string | null;
};

export type SiteConditionMeasurementDefinition = {
  source: string | null;
  kind: string | null;
  scopeType: string | null;
  scopeName: string | null;
  gateZoneId: string | null;
  policyId: string | null;
};

export type ConditionLifecycleIncidentSummary = {
  incidentId: string;
  triggerKind: 'IMMEDIATE' | 'TEMPORAL_PATTERN';
  severity: string | null;
  state: string | null;
  occurredAt: string | null;
  observedCount: number | null;
  patternRule: string | null;
  patternMinEvents: number | null;
  patternWindowSeconds: number | null;
};

export type ConditionLifecyclePostControlPattern = {
  status: 'no_control' | 'none_in_measured_period' | 'continued';
  count: number;
  latestIncidentId: string | null;
  latestOccurredAt: string | null;
  coverageState: string | null;
  finality: string | null;
  summary: string | null;
};

export type ConditionLifecycleScanSummary = {
  scanDefinitionId: string;
  controlRevisionId: string | null;
  scheduledRunId: string | null;
  beforeReportId: string | null;
  afterReportId: string | null;
  runStatus: string | null;
  resultDirection: string | null;
  coverageState: string | null;
  finality: string | null;
  evaluatedAt: string | null;
  motivatingIncidents: Array<{
    incidentId: string;
    triggerKind: 'IMMEDIATE' | 'TEMPORAL_PATTERN';
  }>;
};

export type ConditionLifecycleSummary = {
  latestImmediateIncident: ConditionLifecycleIncidentSummary | null;
  latestTemporalPatternIncident: ConditionLifecycleIncidentSummary | null;
  motivatingIncidentIds: string[];
  controlRevisionId: string | null;
  sinceStartedAt: string | null;
  latestScan: ConditionLifecycleScanSummary | null;
  lifecycleHref: string | null;
  lifecycleUiPath: string | null;
  populationExplanation: string | null;
  postControlPattern: ConditionLifecyclePostControlPattern | null;
};

export type CustomerConditionHealth = {
  measurementHealth: string;
  measurementLastSeenAt: string | null;
  videoHealth: string;
  videoLastSeenAt: string | null;
  measurementLabel: string;
  videoLabel: string;
  rendersCurrentClear: boolean;
  nowClaimsCurrentHealthy: boolean;
  historicalEvidenceAccessible: boolean;
  evidenceNote: string | null;
};

export type SiteConditionState = {
  conditionId: string;
  conditionFamily: string;
  semanticIdentityKey: string;
  canonicalScope?: {
    areaId: string | null;
    zoneIds: string[];
    cameraIds: string[];
    deviceIds: string[];
    policyIds: string[];
  };
  conditionCurrent: {
    lifecycleState: string;
    aggregateRevision: number;
    projectionRevision: number;
  };
  operationalMeasurements: {
    now: SiteConditionMeasurementWindow;
    overTime: Record<string, SiteConditionMeasurementWindow>;
    sinceControl: SiteConditionMeasurementWindow;
  };
  currentActivity?: SiteConditionCurrentActivity | null;
  customerHealth?: CustomerConditionHealth | null;
  operationalScope?: SiteConditionOperationalScope | null;
  measurementDefinition?: SiteConditionMeasurementDefinition | null;
  conditionLifecycle?: ConditionLifecycleSummary | null;
} & Partial<SiteConditionAttentionState>;

export type SuppressedDeviceScopeOrphanTwin = {
  conditionId: string;
  conditionFamily: string | null;
  physicalScopeKey: string | null;
  survivorConditionIds: string[];
  reason: string | null;
};

export type SiteConditionStateDiagnostics = {
  suppressedDeviceScopeOrphanTwins: SuppressedDeviceScopeOrphanTwin[];
};

export type OverviewConditionReadFailure = {
  conditionId: string;
  httpStatus: number | null;
  code: 'http_error' | 'as_of_mismatch' | 'condition_mismatch' | 'scope_mismatch';
};

export type SiteConditionStateResponse = {
  ok: boolean;
  orgId: string;
  siteId: string;
  queryAsOf: string;
  conditionCount: number;
  conditions: SiteConditionState[];
  identityOnly?: boolean;
  diagnostics?: SiteConditionStateDiagnostics;
  /** complete: every required read matched this scope and as-of. partial: some did not. */
  readCompleteness?: 'complete' | 'partial';
  /** False when a site-wide total would be computed from an incomplete condition set. */
  siteTotalsAuthoritative?: boolean;
  failedReads?: OverviewConditionReadFailure[];
};

export type ContinuousSafetyFetchResult<T> =
  | { status: 'success'; data: T }
  | { status: 'disabled' }
  | { status: 'error'; httpStatus: number | null; message: string };

type ApiBucket = {
  measurement_bucket_id?: string;
  window_kind?: string;
  resolution?: string;
  activity_classification?: string;
  measurement_value?: number | null;
  usable_camera_seconds?: number | null;
  expected_camera_seconds?: number | null;
  display_state?: string;
  freshness_state?: string;
  bucket_finality?: string;
  is_partial?: boolean;
  observed_through?: string | null;
  display_as_of?: string | null;
  interval?: { start?: string | null; end?: string | null } | null;
  reason_codes?: string[];
};

type ApiOperationalMeasurementProvenance = {
  window_id?: string | null;
  window_start?: string | null;
  window_end?: string | null;
  scope_id?: string | null;
  condition_id?: string | null;
  condition_definition_version?: string | null;
  episode_definition_version?: string | null;
  denominator_policy_version?: string | null;
  inclusion_policy_version?: string | null;
  source_revision?: string | null;
  unavailable_reason?: string | null;
};

type ApiWindow = {
  availability?: string;
  display_state?: string;
  comparison_status?: string;
  window_anchor_id?: string | null;
  anchor_started_at?: string | null;
  display_as_of?: string | null;
  observed_through?: string | null;
  history_as_of?: string | null;
  comparison_baseline_coverage?: Record<string, unknown> | null;
  update_mode?: string | null;
  next_trigger?: string | null;
  next_trigger_null_reason?: string | null;
  source_age?: string | number | null;
  stage_latency?: string | number | null;
  basis?: {
    type?: string;
    units?: string;
    version?: string | null;
    measurement_epoch?: string | null;
  } | null;
  operational_measurement_provenance?: ApiOperationalMeasurementProvenance | null;
  summary?: {
    bucket_count?: number;
    measurement_value?: number | null;
    activity_counts?: Record<string, number>;
    measurement_coverage?: {
      measurement_status?: string;
      usable_hours?: number | null;
      expected_hours?: number | null;
      coverage_pct?: number | null;
      reviewed_observation_count?: number | null;
      rate_per_hour?: number | null;
      coverage_gate_passed?: boolean | null;
      review_gate_passed?: boolean | null;
      blocked_by?: string[];
      pending_review_count?: number | null;
      confirmed_count?: number | null;
      rejected_count?: number | null;
      item_count?: number | null;
      review_window_state?: string | null;
      review_set_id?: string | null;
      review_complete_through?: string | null;
      oldest_pending_item_time?: string | null;
      producer_staleness?: boolean | null;
      telemetry_missing_since?: string | null;
      telemetry_missing_hours?: number | null;
      basis_version?: string | null;
      basis_id?: string | null;
      frozen_basis?: {
        condition_id?: string | null;
        canonical_scope?: Record<string, unknown>;
        window_start?: string | null;
        window_end?: string | null;
        basis_type?: string | null;
        basis_units?: string | null;
        overlap_dedup_rule?: string | null;
        basis_version?: string | null;
        basis_id?: string | null;
        site_timezone?: string | null;
        schedule_timezone?: string | null;
        shift_calendar_version?: string | null;
        schedule_cadence?: string | null;
        expected_hours_source?: string | null;
        usable_hours_source?: string | null;
        reviewed_observation_count_source?: string | null;
      } | null;
      shortfall_reason_breakdown?: Record<string, {
        bucket_count?: number;
        expected_hours?: number | null;
        usable_hours?: number | null;
        shortfall_hours?: number | null;
      }>;
      coverage_partition?: {
        usable_seconds?: number;
        telemetry_unavailable_seconds?: number;
        unanalyzable_seconds?: number;
        source_unavailable_seconds?: number;
        unknown_seconds?: number;
        usable_hours?: number | null;
        telemetry_unavailable_hours?: number | null;
        unanalyzable_hours?: number | null;
        source_unavailable_hours?: number | null;
        unknown_hours?: number | null;
      } | null;
      coverage_diagnostics?: Array<{
        code?: string;
        affected_seconds?: number | null;
        affected_hours?: number | null;
        bucket_count?: number;
        overlaps_partition?: boolean;
      }>;
      coverage_reconciliation?: {
        reconciled?: boolean | null;
        scheduled_seconds?: number | null;
        partition_seconds?: number | null;
        tolerance_seconds?: number | null;
        delta_seconds?: number | null;
        invalid_reason?: string | null;
      } | null;
      coverage_reconciliation_invalid?: boolean | null;
    } | null;
  };
  buckets?: ApiBucket[];
  reason_codes?: string[];
  recurrence_basis?: {
    schema_version?: number;
    window_id?: string;
    measured_periods?: number;
    expected_periods?: number | null;
    history_completeness?: string;
    periods_with_activity?: number;
    periods_with_explicit_zero?: number;
    occurrence_measured_seconds?: number;
    recurring_rate_state?: string;
    activity_present?: boolean;
    supporting_activity_total?: number | null;
    recorded_occurrence_total?: number | null;
    usable_camera_seconds?: number | null;
    usable_hours?: number | null;
    recurring_rate?: number | null;
    included_bucket_count?: number | null;
    window_start?: string | null;
    window_end?: string | null;
    basis_end?: string | null;
    material_token?: string | null;
    basis?: string;
  } | null;
};

type ApiMeasurementCoverage = NonNullable<
  NonNullable<ApiWindow['summary']>['measurement_coverage']
>;

function mapBucket(row: ApiBucket): SiteConditionMeasurementBucket {
  return {
    measurementBucketId: String(row.measurement_bucket_id || ''),
    windowKind: String(row.window_kind || ''),
    resolution: String(row.resolution || ''),
    activityClassification: String(row.activity_classification || ''),
    measurementValue:
      row.measurement_value == null ? null : Number(row.measurement_value),
    usableCameraSeconds:
      row.usable_camera_seconds == null ? null : Number(row.usable_camera_seconds),
    expectedCameraSeconds:
      row.expected_camera_seconds == null ? null : Number(row.expected_camera_seconds),
    displayState: String(row.display_state || 'unavailable'),
    freshnessState: String(row.freshness_state || 'never_measured'),
    bucketFinality: String(row.bucket_finality || 'provisional'),
    isPartial: Boolean(row.is_partial),
    observedThrough: row.observed_through || null,
    displayAsOf: row.display_as_of || null,
    interval: row.interval && (row.interval.start || row.interval.end)
      ? {
          start: row.interval.start || null,
          end: row.interval.end || null,
        }
      : null,
    reasonCodes: Array.isArray(row.reason_codes) ? [...row.reason_codes] : [],
  };
}

function mapMeasurementCoverage(
  row: ApiMeasurementCoverage | null,
): SiteConditionMeasurementCoverage | null {
  if (!row) {
    return null;
  }
  const frozenBasis = row.frozen_basis;
  const gateFields: Partial<SiteConditionMeasurementCoverage> = {
    ...(row.coverage_gate_passed === undefined
      ? {}
      : {
          coverageGatePassed:
            row.coverage_gate_passed == null ? null : Boolean(row.coverage_gate_passed),
        }),
    ...(row.review_gate_passed === undefined
      ? {}
      : {
          reviewGatePassed:
            row.review_gate_passed == null ? null : Boolean(row.review_gate_passed),
        }),
    ...(row.blocked_by === undefined
      ? {}
      : { blockedBy: Array.isArray(row.blocked_by) ? [...row.blocked_by] : [] }),
    ...(row.pending_review_count === undefined
      ? {}
      : {
          pendingReviewCount:
            row.pending_review_count == null ? null : Number(row.pending_review_count),
        }),
    ...(row.confirmed_count === undefined
      ? {}
      : {
          confirmedCount:
            row.confirmed_count == null ? null : Number(row.confirmed_count),
        }),
    ...(row.rejected_count === undefined
      ? {}
      : {
          rejectedCount:
            row.rejected_count == null ? null : Number(row.rejected_count),
        }),
    ...(row.item_count === undefined
      ? {}
      : {
          itemCount: row.item_count == null ? null : Number(row.item_count),
        }),
    ...(row.review_set_id === undefined
      ? {}
      : { reviewSetId: row.review_set_id == null ? null : String(row.review_set_id) }),
    ...(row.review_window_state === undefined
      ? {}
      : {
          reviewWindowState:
            row.review_window_state == null ? null : String(row.review_window_state),
        }),
    ...(row.review_complete_through === undefined
      ? {}
      : { reviewCompleteThrough: row.review_complete_through ?? null }),
    ...(row.oldest_pending_item_time === undefined
      ? {}
      : { oldestPendingItemTime: row.oldest_pending_item_time ?? null }),
  };
  return {
    measurementStatus: String(row.measurement_status || 'NOT_MET'),
    usableHours: row.usable_hours == null ? null : Number(row.usable_hours),
    expectedHours: row.expected_hours == null ? null : Number(row.expected_hours),
    coveragePct: row.coverage_pct == null ? null : Number(row.coverage_pct),
    reviewedObservationCount:
      row.reviewed_observation_count == null ? null : Number(row.reviewed_observation_count),
    ratePerHour: row.rate_per_hour == null ? null : Number(row.rate_per_hour),
    ...(row.producer_staleness === undefined
      ? {}
      : {
          producerStaleness:
            row.producer_staleness == null ? null : Boolean(row.producer_staleness),
        }),
    telemetryMissingSince: row.telemetry_missing_since == null
      ? null
      : String(row.telemetry_missing_since),
    telemetryMissingHours: row.telemetry_missing_hours == null
      ? null
      : Number(row.telemetry_missing_hours),
    basisVersion: row.basis_version ?? null,
    basisId: row.basis_id ?? null,
    ...gateFields,
    frozenBasis: frozenBasis
      ? {
          conditionId: frozenBasis.condition_id ?? null,
          canonicalScope: frozenBasis.canonical_scope || {},
          windowStart: frozenBasis.window_start ?? null,
          windowEnd: frozenBasis.window_end ?? null,
          basisType: frozenBasis.basis_type ?? null,
          basisUnits: frozenBasis.basis_units ?? null,
          overlapDedupRule: frozenBasis.overlap_dedup_rule ?? null,
          basisVersion: frozenBasis.basis_version ?? null,
          basisId: frozenBasis.basis_id ?? null,
          siteTimezone: frozenBasis.site_timezone ?? null,
          scheduleTimezone: frozenBasis.schedule_timezone ?? null,
          shiftCalendarVersion: frozenBasis.shift_calendar_version ?? null,
          scheduleCadence: frozenBasis.schedule_cadence ?? null,
          expectedHoursSource: frozenBasis.expected_hours_source ?? null,
          usableHoursSource: frozenBasis.usable_hours_source ?? null,
          reviewedObservationCountSource:
            frozenBasis.reviewed_observation_count_source ?? null,
        }
      : null,
    shortfallReasonBreakdown: Object.fromEntries(
      Object.entries(row.shortfall_reason_breakdown || {}).map(([reason, value]) => [
        reason,
        {
          bucketCount: Number(value?.bucket_count ?? 0),
          expectedHours: value?.expected_hours == null ? null : Number(value.expected_hours),
          usableHours: value?.usable_hours == null ? null : Number(value.usable_hours),
          shortfallHours:
            value?.shortfall_hours == null ? null : Number(value.shortfall_hours),
        },
      ]),
    ),
    coveragePartition: row.coverage_partition
      ? {
          usableSeconds: Number(row.coverage_partition.usable_seconds ?? 0),
          telemetryUnavailableSeconds: Number(
            row.coverage_partition.telemetry_unavailable_seconds ?? 0,
          ),
          unanalyzableSeconds: Number(row.coverage_partition.unanalyzable_seconds ?? 0),
          sourceUnavailableSeconds: Number(
            row.coverage_partition.source_unavailable_seconds ?? 0,
          ),
          unknownSeconds: Number(row.coverage_partition.unknown_seconds ?? 0),
          usableHours: row.coverage_partition.usable_hours == null
            ? null
            : Number(row.coverage_partition.usable_hours),
          telemetryUnavailableHours:
            row.coverage_partition.telemetry_unavailable_hours == null
              ? null
              : Number(row.coverage_partition.telemetry_unavailable_hours),
          unanalyzableHours: row.coverage_partition.unanalyzable_hours == null
            ? null
            : Number(row.coverage_partition.unanalyzable_hours),
          sourceUnavailableHours:
            row.coverage_partition.source_unavailable_hours == null
              ? null
              : Number(row.coverage_partition.source_unavailable_hours),
          unknownHours: row.coverage_partition.unknown_hours == null
            ? null
            : Number(row.coverage_partition.unknown_hours),
        }
      : null,
    coverageDiagnostics: (row.coverage_diagnostics || []).map((item) => ({
      code: String(item?.code || ''),
      affectedSeconds: item?.affected_seconds == null ? null : Number(item.affected_seconds),
      affectedHours: item?.affected_hours == null ? null : Number(item.affected_hours),
      bucketCount: Number(item?.bucket_count ?? 0),
      overlapsPartition: Boolean(item?.overlaps_partition),
    })),
    coverageReconciliation: row.coverage_reconciliation
      ? {
          reconciled: row.coverage_reconciliation.reconciled ?? null,
          scheduledSeconds: row.coverage_reconciliation.scheduled_seconds == null
            ? null
            : Number(row.coverage_reconciliation.scheduled_seconds),
          partitionSeconds: row.coverage_reconciliation.partition_seconds == null
            ? null
            : Number(row.coverage_reconciliation.partition_seconds),
          toleranceSeconds: row.coverage_reconciliation.tolerance_seconds == null
            ? null
            : Number(row.coverage_reconciliation.tolerance_seconds),
          deltaSeconds: row.coverage_reconciliation.delta_seconds == null
            ? null
            : Number(row.coverage_reconciliation.delta_seconds),
          invalidReason: row.coverage_reconciliation.invalid_reason == null
            ? null
            : String(row.coverage_reconciliation.invalid_reason),
        }
      : null,
    coverageReconciliationInvalid: Boolean(row.coverage_reconciliation_invalid),
  };
}

/**
 * Supporting-facts total from bucket rows only.
 * Never use summary.measurementValue here — after the review boundary that field
 * is claim-only (reviewed numerator / null) and must not gate facts.
 *
 * Safe for Current COW (closed operating-window PT15M set). For Recurring 1D
 * (`rolling_24h`), prefer {@link recurringSupportingActivityTotal} so sparse /
 * anchored days cannot invent a wall-clock "1 day" Σ.
 */
export function supportingActivityTotal(
  window: SiteConditionMeasurementWindow | null | undefined,
): number | null {
  let sum = 0;
  let found = false;
  for (const bucket of window?.buckets || []) {
    if (bucket.measurementValue != null && Number.isFinite(bucket.measurementValue)) {
      sum += Number(bucket.measurementValue);
      found = true;
    }
  }
  return found ? sum : null;
}

/** Full PT15M day = 96 slots. Require ≥18h before calling a Σ a day total. */
export const ROLLING_24H_MIN_PERIODS_FOR_DAY_TOTAL = 72;

export const ROLLING_24H_ANCHORED_REASON =
  'rolling_window_anchored_to_observed_through';

/**
 * True when Recurring 1D may honestly Σ PT15M bucket increments as a day total.
 * Fail closed when the window was re-bounded to last observed data (not
 * wall-clock today) or when measured slots are too sparse for a day claim.
 */
export function rolling24hEpisodeDayTotalAllowed(
  window: SiteConditionMeasurementWindow | null | undefined,
): boolean {
  if (!window) return false;
  if ((window.reasonCodes || []).includes(ROLLING_24H_ANCHORED_REASON)) {
    return false;
  }
  const periods = window.summary.bucketCount || window.buckets.length || 0;
  if (periods < ROLLING_24H_MIN_PERIODS_FOR_DAY_TOTAL) return false;
  const resolution = window.buckets[0]?.resolution || 'PT15M';
  // Only PT15M source rows need the dense-day gate; other resolutions keep Σ.
  if (resolution === 'PT15M') return true;
  return periods > 0;
}

/**
 * Recurring episode activity total with fail-closed 1D semantics.
 * - `rolling_24h`: Σ only for a complete, non-anchored measured day.
 * - 7d/30d/90d: keep bucket Σ (PT1H rollups are already period increments).
 */
export function recurringSupportingActivityTotal(
  window: SiteConditionMeasurementWindow | null | undefined,
  windowId: string | null | undefined,
): number | null {
  if (!window) return null;
  if (windowId === 'rolling_24h') {
    if (!rolling24hEpisodeDayTotalAllowed(window)) return null;
  }
  return supportingActivityTotal(window);
}

/** Recorded occurrences are facts, separate from rate/day-total eligibility. */
export function recurringRecordedOccurrenceTotal(
  window: SiteConditionMeasurementWindow | null | undefined,
): number | null {
  if (!window?.recurrenceBasis) return null;
  const finalized = window.recurrenceBasis.recordedOccurrenceTotal;
  if (finalized != null && Number.isFinite(finalized)) {
    return finalized;
  }
  const basisTotal = window.recurrenceBasis.supportingActivityTotal;
  if (basisTotal != null && Number.isFinite(basisTotal)) {
    return basisTotal;
  }
  return null;
}

/**
 * Recurring usable hours from the same finalized OM buckets as
 * {@link recurringRecordedOccurrenceTotal}. Never the coverage envelope.
 */
export function recurringUsableHours(
  window: SiteConditionMeasurementWindow | null | undefined,
): number | null {
  const basis = window?.recurrenceBasis;
  if (!basis) return null;
  if (basis.usableHours != null && Number.isFinite(basis.usableHours) && basis.usableHours > 0) {
    return basis.usableHours;
  }
  if (
    basis.usableCameraSeconds != null
    && Number.isFinite(basis.usableCameraSeconds)
    && basis.usableCameraSeconds > 0
  ) {
    return basis.usableCameraSeconds / 3600;
  }
  return null;
}

export function recurringRateFromSameBuckets(
  window: SiteConditionMeasurementWindow | null | undefined,
): number | null {
  const basis = window?.recurrenceBasis;
  if (!basis) return null;
  const count = recurringRecordedOccurrenceTotal(window);
  const hours = recurringUsableHours(window);
  if (count == null || hours == null || !(hours > 0)) return null;
  return count / hours;
}

/** True when the window has any supporting posture (bucket totals or activity counts). */
export function hasSupportingActivityFacts(
  window: SiteConditionMeasurementWindow | null | undefined,
): boolean {
  if (supportingActivityTotal(window) != null) return true;
  return Object.values(window?.summary.activityCounts || {}).some(
    (count) => typeof count === 'number' && Number.isFinite(count) && count > 0,
  );
}

function mapOperationalMeasurementProvenance(
  row: ApiOperationalMeasurementProvenance | null | undefined,
): OperationalMeasurementProvenance | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const textValue = (value: unknown): string | null => {
    if (value == null) return null;
    const out = String(value).trim();
    return out || null;
  };
  return {
    windowId: textValue(row.window_id),
    windowStart: textValue(row.window_start),
    windowEnd: textValue(row.window_end),
    scopeId: textValue(row.scope_id),
    conditionId: textValue(row.condition_id),
    conditionDefinitionVersion: textValue(row.condition_definition_version),
    episodeDefinitionVersion: textValue(row.episode_definition_version),
    denominatorPolicyVersion: textValue(row.denominator_policy_version),
    inclusionPolicyVersion: textValue(row.inclusion_policy_version),
    sourceRevision: textValue(row.source_revision),
    unavailableReason: textValue(row.unavailable_reason),
  };
}

/** Compact identity fingerprint — changes when twin/window identity changes. */
export function operationalMeasurementIdentityKey(
  provenance: OperationalMeasurementProvenance | null | undefined,
): string | null {
  if (!provenance) return null;
  const parts = [
    provenance.windowId,
    provenance.scopeId,
    provenance.conditionId,
    provenance.conditionDefinitionVersion,
    provenance.episodeDefinitionVersion,
    provenance.denominatorPolicyVersion,
    provenance.inclusionPolicyVersion,
    provenance.sourceRevision,
  ];
  if (parts.every((part) => part == null)) return null;
  return parts.map((part) => part ?? '').join('|');
}

function mapRecurrenceBasis(
  row: ApiWindow['recurrence_basis'] | undefined,
): SiteConditionRecurrenceBasis | null {
  if (!row || typeof row !== 'object') return null;
  return {
    schemaVersion: Number(row.schema_version ?? 1),
    windowId: String(row.window_id || ''),
    measuredPeriods: Number(row.measured_periods ?? 0),
    expectedPeriods:
      row.expected_periods == null ? null : Number(row.expected_periods),
    historyCompleteness: String(row.history_completeness || ''),
    periodsWithActivity: Number(row.periods_with_activity ?? 0),
    periodsWithExplicitZero: Number(row.periods_with_explicit_zero ?? 0),
    occurrenceMeasuredSeconds: Number(row.occurrence_measured_seconds ?? 0),
    recurringRateState: String(row.recurring_rate_state || ''),
    activityPresent: Boolean(row.activity_present),
    supportingActivityTotal:
      row.supporting_activity_total == null
        ? null
        : Number(row.supporting_activity_total),
    recordedOccurrenceTotal:
      row.recorded_occurrence_total == null
        ? null
        : Number(row.recorded_occurrence_total),
    usableCameraSeconds:
      row.usable_camera_seconds == null
        ? null
        : Number(row.usable_camera_seconds),
    usableHours:
      row.usable_hours == null ? null : Number(row.usable_hours),
    recurringRate:
      row.recurring_rate == null ? null : Number(row.recurring_rate),
    includedBucketCount:
      row.included_bucket_count == null
        ? null
        : Number(row.included_bucket_count),
    windowStart: row.window_start == null ? null : String(row.window_start),
    windowEnd: row.window_end == null ? null : String(row.window_end),
    basisEnd: row.basis_end == null ? null : String(row.basis_end),
    materialToken: row.material_token == null ? null : String(row.material_token),
    basis: String(row.basis || ''),
  };
}

export function mapMeasurementWindow(row: ApiWindow | undefined): SiteConditionMeasurementWindow {
  const basis = row?.basis;
  const firstBucket = Array.isArray(row?.buckets) && row!.buckets.length > 0 ? row!.buckets[0] : null;
  return {
    availability: String(row?.availability || 'unavailable'),
    displayState: String(row?.display_state || 'unavailable'),
    comparisonStatus: row?.comparison_status,
    windowAnchorId: row?.window_anchor_id ?? null,
    anchorStartedAt: row?.anchor_started_at ?? null,
    displayAsOf: row?.display_as_of ?? firstBucket?.display_as_of ?? null,
    observedThrough: row?.observed_through ?? firstBucket?.observed_through ?? null,
    historyAsOf: row?.history_as_of
      ?? row?.operational_measurement_provenance?.window_end
      ?? null,
    // Passthrough only — undefined when absent (do not default).
    updateMode: row?.update_mode === undefined ? undefined : row.update_mode,
    nextTrigger: row?.next_trigger === undefined ? undefined : row.next_trigger,
    nextTriggerNullReason:
      row?.next_trigger_null_reason === undefined ? undefined : row.next_trigger_null_reason,
    sourceAge: row?.source_age === undefined ? undefined : row.source_age,
    stageLatency: row?.stage_latency === undefined ? undefined : row.stage_latency,
    basis: basis
      ? {
          type: String(basis.type || ''),
          units: String(basis.units || ''),
          version: basis.version ?? null,
          measurementEpoch: basis.measurement_epoch ?? null,
        }
      : null,
    operationalMeasurementProvenance: mapOperationalMeasurementProvenance(
      row?.operational_measurement_provenance,
    ),
    comparisonBaselineCoverage: row?.comparison_baseline_coverage
      ? mapMeasurementCoverage(row.comparison_baseline_coverage as never)
      : undefined,
    summary: {
      bucketCount: Number(row?.summary?.bucket_count ?? 0),
      measurementValue:
        row?.summary?.measurement_value == null
          ? null
          : Number(row.summary.measurement_value),
      activityCounts: Object.fromEntries(
        Object.entries(row?.summary?.activity_counts || {}).map(
          ([key, value]) => [key, Number(value)],
        ),
      ),
      measurementCoverage: mapMeasurementCoverage(row?.summary?.measurement_coverage || null),
    },
    buckets: Array.isArray(row?.buckets) ? row!.buckets.map(mapBucket) : [],
    reasonCodes: Array.isArray(row?.reason_codes) ? [...row!.reason_codes] : [],
    recurrenceBasis: mapRecurrenceBasis(row?.recurrence_basis),
  };
}

function mapOperationalScope(row: unknown): SiteConditionOperationalScope | null {
  if (!row || typeof row !== 'object') return null;
  const raw = row as Record<string, unknown>;
  return {
    areaId: raw.area_id == null ? null : String(raw.area_id),
    deviceId: raw.device_id == null ? null : String(raw.device_id),
    cameraId: raw.camera_id == null ? null : String(raw.camera_id),
  };
}

function mapMeasurementDefinition(row: unknown): SiteConditionMeasurementDefinition | null {
  if (!row || typeof row !== 'object') return null;
  const raw = row as Record<string, unknown>;
  return {
    source: raw.source == null ? null : String(raw.source),
    kind: raw.kind == null ? null : String(raw.kind),
    scopeType: raw.scope_type == null ? null : String(raw.scope_type),
    scopeName: raw.scope_name == null ? null : String(raw.scope_name),
    gateZoneId: raw.gate_zone_id == null ? null : String(raw.gate_zone_id),
    policyId: raw.policy_id == null ? null : String(raw.policy_id),
  };
}

function mapCustomerHealth(row: unknown): CustomerConditionHealth | null {
  if (!row || typeof row !== 'object') return null;
  const raw = row as Record<string, unknown>;
  const measurementHealth = String(raw.measurement_health || '').trim();
  const videoHealth = String(raw.video_health || '').trim();
  if (!measurementHealth && !videoHealth) return null;
  return {
    measurementHealth,
    measurementLastSeenAt: raw.measurement_last_seen_at == null ? null : String(raw.measurement_last_seen_at),
    videoHealth,
    videoLastSeenAt: raw.video_last_seen_at == null ? null : String(raw.video_last_seen_at),
    measurementLabel: String(raw.measurement_label || ''),
    videoLabel: String(raw.video_label || ''),
    rendersCurrentClear: raw.renders_current_clear === true,
    nowClaimsCurrentHealthy: raw.now_claims_current_healthy === true,
    historicalEvidenceAccessible: raw.historical_evidence_accessible !== false,
    evidenceNote: raw.evidence_note == null ? null : String(raw.evidence_note),
  };
}

function mapCurrentActivity(row: unknown): SiteConditionCurrentActivity | null {
  if (!row || typeof row !== 'object') return null;
  const raw = row as Record<string, unknown>;
  const availabilityRaw = String(raw.availability || '').trim();
  const availability: SiteConditionCurrentActivity['availability'] = availabilityRaw === 'available' || availabilityRaw === 'no_activity'
    ? availabilityRaw
    : 'unavailable';
  const countRaw = raw.occurrence_count_so_far;
  const count = typeof countRaw === 'number' && Number.isFinite(countRaw)
    ? countRaw
    : (countRaw == null ? null : Number(countRaw));
  const occurrenceCountSoFar = count != null && Number.isFinite(count) ? count : null;
  return {
    conditionId: raw.condition_id == null ? null : String(raw.condition_id),
    cameraId: raw.camera_id == null ? null : String(raw.camera_id),
    zoneId: raw.zone_id == null ? null : String(raw.zone_id),
    policyId: raw.policy_id == null ? null : String(raw.policy_id),
    windowStart: raw.window_start == null ? null : String(raw.window_start),
    queryAsOf: raw.query_as_of == null ? null : String(raw.query_as_of),
    occurrenceCountSoFar: availability === 'unavailable' ? null : occurrenceCountSoFar,
    latestQualifiedAt: raw.latest_qualified_at == null ? null : String(raw.latest_qualified_at),
    provisional: true,
    availability,
    reasonCodes: Array.isArray(raw.reason_codes)
      ? raw.reason_codes.map((code) => String(code || '').trim()).filter(Boolean)
      : [],
  };
}

function mapLifecycleIncident(row: unknown): ConditionLifecycleIncidentSummary | null {
  if (!row || typeof row !== 'object') return null;
  const raw = row as Record<string, unknown>;
  const triggerKind = String(raw.trigger_kind || '').toUpperCase();
  if (triggerKind !== 'IMMEDIATE' && triggerKind !== 'TEMPORAL_PATTERN') return null;
  const metadata = (
    raw.envelope_metadata && typeof raw.envelope_metadata === 'object'
      ? raw.envelope_metadata
      : {}
  ) as Record<string, unknown>;
  const minEvents = metadata.min_events == null ? null : Number(metadata.min_events);
  const windowSeconds = metadata.window_seconds == null ? null : Number(metadata.window_seconds);
  return {
    incidentId: String(raw.incident_id || ''),
    triggerKind,
    severity: raw.severity == null ? null : String(raw.severity),
    state: raw.status == null ? (raw.edge_state == null ? null : String(raw.edge_state)) : String(raw.status),
    occurredAt: raw.ts_start == null ? null : String(raw.ts_start),
    observedCount: raw.observed_count == null ? null : Number(raw.observed_count),
    patternRule: metadata.min_events == null
      ? null
      : `${String(metadata.min_events)} in ${String(metadata.window_seconds || '—')}s`,
    patternMinEvents: minEvents != null && Number.isFinite(minEvents) ? minEvents : null,
    patternWindowSeconds: windowSeconds != null && Number.isFinite(windowSeconds) ? windowSeconds : null,
  };
}

function mapConditionLifecycle(row: unknown): ConditionLifecycleSummary | null {
  if (!row || typeof row !== 'object') return null;
  const raw = row as Record<string, unknown>;
  const control = (
    raw.recorded_control && typeof raw.recorded_control === 'object'
      ? raw.recorded_control
      : {}
  ) as Record<string, unknown>;
  const since = (
    raw.since && typeof raw.since === 'object' ? raw.since : {}
  ) as Record<string, unknown>;
  const scan = (
    raw.latest_scan && typeof raw.latest_scan === 'object' ? raw.latest_scan : null
  ) as Record<string, unknown> | null;
  const reconciliation = (
    raw.population_reconciliation && typeof raw.population_reconciliation === 'object'
      ? raw.population_reconciliation
      : {}
  ) as Record<string, unknown>;
  return {
    latestImmediateIncident: mapLifecycleIncident(raw.latest_immediate_incident),
    latestTemporalPatternIncident: mapLifecycleIncident(raw.latest_temporal_pattern_incident),
    motivatingIncidentIds: Array.isArray(control.motivating_incident_ids)
      ? control.motivating_incident_ids.map(String)
      : [],
    controlRevisionId: control.control_revision_id == null
      ? null
      : String(control.control_revision_id),
    sinceStartedAt: since.anchor_started_at == null ? null : String(since.anchor_started_at),
    latestScan: scan
      ? {
          scanDefinitionId: String(scan.scan_definition_id || ''),
          controlRevisionId: scan.control_revision_id == null
            ? null
            : String(scan.control_revision_id),
          scheduledRunId: scan.scheduled_run_id == null ? null : String(scan.scheduled_run_id),
          beforeReportId: scan.before_report_id == null ? null : String(scan.before_report_id),
          afterReportId: scan.after_report_id == null ? null : String(scan.after_report_id),
          runStatus: scan.run_status == null ? null : String(scan.run_status),
          resultDirection: scan.result_direction == null ? null : String(scan.result_direction),
          coverageState: scan.coverage_state == null ? null : String(scan.coverage_state),
          finality: scan.finality == null ? null : String(scan.finality),
          evaluatedAt: scan.evaluated_at == null ? null : String(scan.evaluated_at),
          motivatingIncidents: Array.isArray(scan.motivating_incidents)
            ? scan.motivating_incidents.flatMap((item) => {
                if (!item || typeof item !== 'object') return [];
                const incident = item as Record<string, unknown>;
                const triggerKind = String(incident.trigger_kind || '').toUpperCase();
                if (triggerKind !== 'IMMEDIATE' && triggerKind !== 'TEMPORAL_PATTERN') {
                  return [];
                }
                return [{
                  incidentId: String(incident.incident_id || ''),
                  triggerKind,
                }];
              })
            : [],
        }
      : null,
    lifecycleHref: raw.lifecycle_href == null ? null : String(raw.lifecycle_href),
    lifecycleUiPath: raw.lifecycle_ui_path == null ? null : String(raw.lifecycle_ui_path),
    populationExplanation: reconciliation.explanation == null
      ? null
      : String(reconciliation.explanation),
    postControlPattern: mapPostControlPattern(raw.post_control_pattern),
  };
}

function mapPostControlPattern(row: unknown): ConditionLifecyclePostControlPattern | null {
  if (!row || typeof row !== 'object') return null;
  const raw = row as Record<string, unknown>;
  const status = String(raw.status || '').trim();
  if (status !== 'no_control' && status !== 'none_in_measured_period' && status !== 'continued') {
    return null;
  }
  return {
    status,
    count: Number(raw.count || 0),
    latestIncidentId: raw.latest_incident_id == null ? null : String(raw.latest_incident_id),
    latestOccurredAt: raw.latest_occurred_at == null ? null : String(raw.latest_occurred_at),
    coverageState: raw.coverage_state == null ? null : String(raw.coverage_state),
    finality: raw.finality == null ? null : String(raw.finality),
    summary: raw.summary == null ? null : String(raw.summary),
  };
}

export function mapSiteConditionState(row: Record<string, unknown>): SiteConditionState {
  const measurements = (row.operational_measurements || {}) as Record<string, unknown>;
  const overTime = (measurements.over_time || {}) as Record<string, ApiWindow>;
  const canonicalScope = (row.canonical_scope || {}) as Record<string, unknown>;
  const scopeIds = (key: string): string[] => {
    const value = canonicalScope[key];
    if (Array.isArray(value)) {
      return value.map((item) => String(item || '').trim()).filter(Boolean);
    }
    const single = String(value || '').trim();
    return single ? [single] : [];
  };
  const attentionReasonsRaw = row.attention_reasons;
  const attentionReasons = Array.isArray(attentionReasonsRaw)
    ? attentionReasonsRaw.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  return {
    conditionId: String(row.condition_id || ''),
    conditionFamily: String(row.condition_family || ''),
    semanticIdentityKey: String(row.semantic_identity_key || ''),
    canonicalScope: {
      areaId: String(canonicalScope.area_id || '').trim() || null,
      zoneIds: scopeIds('zone_ids'),
      cameraIds: scopeIds('camera_ids'),
      deviceIds: scopeIds('device_ids'),
      policyIds: scopeIds('policy_ids'),
    },
    conditionCurrent: {
      lifecycleState: String(
        (row.condition_current as { lifecycle_state?: string } | undefined)?.lifecycle_state || '',
      ),
      aggregateRevision: Number(
        (row.condition_current as { aggregate_revision?: number } | undefined)?.aggregate_revision ?? 0,
      ),
      projectionRevision: Number(
        (row.condition_current as { projection_revision?: number } | undefined)?.projection_revision ?? 0,
      ),
    },
    operationalMeasurements: {
      now: mapMeasurementWindow(measurements.now as ApiWindow | undefined),
      overTime: Object.fromEntries(
        Object.entries(overTime).map(([key, value]) => [key, mapMeasurementWindow(value)]),
      ),
      sinceControl: mapMeasurementWindow(measurements.since_control as ApiWindow | undefined),
    },
    currentActivity: mapCurrentActivity(row.current_activity),
    customerHealth: mapCustomerHealth(row.customer_health),
    operationalScope: mapOperationalScope(row.operational_scope),
    measurementDefinition: mapMeasurementDefinition(row.measurement_definition),
    conditionLifecycle: mapConditionLifecycle(row.condition_lifecycle),
    attentionRequired: Boolean(row.attention_required),
    attentionStatus: row.attention_status == null ? null : String(row.attention_status),
    attentionReason: row.attention_reason == null ? null : String(row.attention_reason),
    attentionReasons,
    attentionPolicyId:
      row.attention_policy_id == null ? null : String(row.attention_policy_id),
    attentionPolicyVersion:
      row.attention_policy_version == null
        ? null
        : Number(row.attention_policy_version),
    attentionTriggeredAt:
      row.attention_triggered_at == null ? null : String(row.attention_triggered_at),
    lastAttentionActivityAt:
      row.last_attention_activity_at == null
        ? null
        : String(row.last_attention_activity_at),
    lastReasonUpdatedAt:
      row.last_reason_updated_at == null ? null : String(row.last_reason_updated_at),
    reasonState:
      row.reason_state && typeof row.reason_state === 'object'
        ? (row.reason_state as Record<string, unknown>)
        : {},
    attentionEvaluatedAt:
      row.attention_evaluated_at == null ? null : String(row.attention_evaluated_at),
    attentionScopeKey:
      row.attention_scope_key == null ? null : String(row.attention_scope_key),
    attentionLatchId:
      row.attention_latch_id == null ? null : String(row.attention_latch_id),
    attentionEvidenceRefs:
      row.attention_evidence_refs && typeof row.attention_evidence_refs === 'object'
        ? (row.attention_evidence_refs as Record<string, unknown>)
        : {},
    openWorkRef:
      row.open_work_ref && typeof row.open_work_ref === 'object'
        ? (row.open_work_ref as Record<string, unknown>)
        : null,
  };
}

/**
 * Convert a condition-state query_as_of value into an ISO instant for `as_of`.
 * Flask serializes query_as_of as RFC1123; as_of input requires ISO date-time.
 * Invalid or empty input returns null — never Date.now().
 */
export function normalizeConditionStateAsOf(value: string): string | null {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return null;
  }
  const ms = Date.parse(trimmed);
  if (!Number.isFinite(ms)) {
    return null;
  }
  return new Date(ms).toISOString();
}

export async function fetchSiteConditionState(params: {
  siteId: string;
  conditionIds?: string[];
  sinceControlAnchorId?: string;
  asOf?: string;
  identityOnly?: boolean;
}): Promise<ContinuousSafetyFetchResult<SiteConditionStateResponse>> {
  const search = new URLSearchParams();
  for (const conditionId of params.conditionIds || []) {
    search.append('condition_id', conditionId);
  }
  if (params.sinceControlAnchorId) {
    search.set('since_control_anchor_id', params.sinceControlAnchorId);
  }
  if (params.asOf) {
    search.set('as_of', params.asOf);
  }
  if (params.identityOnly) {
    search.set('identity_only', 'true');
  }
  const query = search.toString();
  const path = `/api/v1/sites/${encodeURIComponent(params.siteId)}/condition-state${
    query ? `?${query}` : ''
  }`;
  try {
    const response = await api.get(path);
    const payload = (response.data || {}) as Record<string, unknown>;
    if (!payload?.ok) {
      return {
        status: 'error',
        httpStatus: response.status || null,
        message: 'Site Condition data returned an invalid response.',
      };
    }
    const diagnosticsPayload = (payload.diagnostics || {}) as Record<string, unknown>;
    const suppressedRaw = diagnosticsPayload.suppressed_device_scope_orphan_twins;
    const suppressedDeviceScopeOrphanTwins = Array.isArray(suppressedRaw)
      ? suppressedRaw.map((row) => {
          const item = (row || {}) as Record<string, unknown>;
          const survivors = item.survivor_condition_ids;
          return {
            conditionId: String(item.condition_id || ''),
            conditionFamily: item.condition_family == null
              ? null
              : String(item.condition_family),
            physicalScopeKey: item.physical_scope_key == null
              ? null
              : String(item.physical_scope_key),
            survivorConditionIds: Array.isArray(survivors)
              ? survivors.map((id) => String(id || '')).filter(Boolean)
              : [],
            reason: item.reason == null ? null : String(item.reason),
          };
        }).filter((row) => row.conditionId)
      : [];
    return {
      status: 'success',
      data: {
        ok: true,
        orgId: String(payload.org_id || ''),
        siteId: String(payload.site_id || ''),
        queryAsOf: String(payload.query_as_of || ''),
        conditionCount: Number(payload.condition_count ?? 0),
        conditions: Array.isArray(payload.conditions)
          ? payload.conditions.map((row) => mapSiteConditionState(row as Record<string, unknown>))
          : [],
        identityOnly: payload.identity_only === true,
        diagnostics: {
          suppressedDeviceScopeOrphanTwins,
        },
      },
    };
  } catch (error) {
    const httpStatus = Number(
      (error as { response?: { status?: number } })?.response?.status || 0,
    ) || null;
    if (httpStatus === 404) {
      return { status: 'disabled' };
    }
    return {
      status: 'error',
      httpStatus,
      message: httpStatus === 401 || httpStatus === 403
        ? 'Site Condition data is not authorized for this session.'
        : 'Site Condition data is temporarily unavailable.',
    };
  }
}
